/**
 * Cards from `/v1` list items, the shape `POST /shop-agent/search/products`
 * (and its internal twin) answers with. Free of client-only imports, so
 * server-rendered grids and bridge routes use it too.
 */

import type { Product } from "@/lib/product-utils";

import type {
  MerchantProductImageMetadataView,
  MerchantProductListItem,
} from "../../openapi/shop_agent/client/types.gen";

/**
 * A `/v1` list item as the product the results grid renders. `images` holds
 * the photos other than `image`: `/v1` lists the primary shot again, often
 * without its hosted copy, and a card's hover would show it twice.
 */
export function productFromListItem(item: MerchantProductListItem): Product {
  const primaryUrl = item.primaryImage?.url;
  const images = (item.images ?? []).filter((view) => view.url !== primaryUrl).map(imageFromView);
  const primary = item.primaryImage ? imageFromView(item.primaryImage) : undefined;
  const product: Product = {
    uuid: item.uuid,
    name: item.title ?? null,
    url: item.productUrl,
    brand_name: item.brand?.name ?? null,
    brand_slug: item.brand?.slug ?? null,
    catalog: item.catalogKey ?? null,
    catalog_display_name: item.catalogDisplayName ?? null,
    current_price: item.currentPrice ?? null,
    original_price: item.originalPrice ?? null,
    is_in_stock: item.inStock ?? null,
    sizes: item.sizes ?? null,
    raw_score: item.rawScore ?? null,
    image: primary ?? images[0] ?? null,
    images,
  };
  if (item.brand) {
    product.brand = { name: item.brand.name, description: item.brand.description ?? null };
  }
  if (item.rating) {
    product.rating = { average_rating: item.rating.average, rating_count: item.rating.count };
  }
  if (!product.image && item.imageUrl) {
    (product as Product & { imageUrl?: string }).imageUrl = item.imageUrl;
  }
  return product;
}

/** The hosted copy goes where the grid looks for it first; the source URL is its fallback. */
function imageFromView(view: MerchantProductImageMetadataView): NonNullable<Product["image"]> {
  return {
    url: view.url,
    gs_url: view.cdnUrl ?? null,
    cdn_url: view.cdnUrl ?? null,
    width: view.width ?? null,
    height: view.height ?? null,
    mime_type: view.mimeType ?? null,
  };
}
